<template>
  <div
    v-if="showQuickLinks"
    id="quick-links-bar"
    :class="barThemeClasses"
    class="secondary-bar"
  >
    <div class="d-flex align-items-center px-1 py-50">
      <!-- Model label -->
      <span
        v-if="model === 'user' && projectName !== 'PS'"
        class="nav-link d-flex align-items-center font-weight-bold mx-1"
      >
        {{ projectName }} User {{ Id }}
      </span>
      <span
        v-if="model === 'survey'"
        class="nav-link d-flex align-items-center font-weight-bold mx-1"
      >
        Survey {{ Id }}
      </span>
      <span
        v-if="model === 'offer'"
        class="nav-link d-flex align-items-center font-weight-bold mx-1"
      >
        Offer {{ Id }}
      </span>

      <template v-if="canAccess($route.permission || $route.name)">
        <b-button
          v-for="link in detailsLinks"
          :key="link.path"
          variant="outline-primary"
          size="sm"
          class="mr-50"
          :to="generateLink(link)"
          tag="a"
          target="_blank"
        >
          {{ getTitle(link) }}
        </b-button>

        <b-dropdown
          v-if="surveyGroupLinks.length"
          ref="surveysDropdown"
          text="Surveys"
          variant="flat-secondary"
          size="sm"
          class="quick-links-dropdown mr-50"
          no-caret
          @mouseenter.native="openDropdown('surveysDropdown')"
          @mouseleave.native="scheduleClose('surveysDropdown')"
        >
          <b-dropdown-item
            v-for="link in surveyGroupLinks"
            :key="link.path"
            v-bind="linkProps(link)"
            target="_blank"
          >
            {{ getTitle(link) }}
          </b-dropdown-item>
        </b-dropdown>

        <b-dropdown
          v-if="offerGroupLinks.length"
          ref="offersDropdown"
          text="Offers"
          variant="flat-secondary"
          size="sm"
          class="quick-links-dropdown mr-50"
          no-caret
          @mouseenter.native="openDropdown('offersDropdown')"
          @mouseleave.native="scheduleClose('offersDropdown')"
        >
          <b-dropdown-item
            v-for="link in offerGroupLinks"
            :key="link.path"
            v-bind="linkProps(link)"
            target="_blank"
          >
            {{ getTitle(link) }}
          </b-dropdown-item>
        </b-dropdown>

        <b-dropdown
          v-if="otherGroupLinks.length"
          ref="otherDropdown"
          text="Other"
          variant="flat-secondary"
          size="sm"
          class="quick-links-dropdown mr-50"
          no-caret
          @mouseenter.native="openDropdown('otherDropdown')"
          @mouseleave.native="scheduleClose('otherDropdown')"
        >
          <b-dropdown-item
            v-for="link in otherGroupLinks"
            :key="link.path"
            v-bind="linkProps(link)"
            target="_blank"
          >
            {{ link.type === 'external' ? getExternalTitle(link) : getTitle(link) }}
          </b-dropdown-item>
        </b-dropdown>

        <!-- Function-type links as standalone buttons -->
        <div v-if="functionTypeLinks.length" class="ml-auto d-flex align-items-center">
          <span class="text-muted mr-75 font-small-3">Quick View:</span>
          <b-button
            v-for="link in functionTypeLinks"
            :key="link.path"
            variant="outline-primary"
            size="sm"
            class="mr-50"
            @click="invokeMethod(link.path)"
          >
            {{ link.meta.title }}
          </b-button>
        </div>
      </template>
    </div>

    <b-modal
        ref="user_profiling_basics_modal"
        size="lg"
        title="User Profiling Basics"
    >
      <UserProfilingBasics :userProfilingBasics="userProfilingBasics"/>
    </b-modal>

    <b-modal
        ref="user_offers_overviews_modal"
        size="xl"
        title="User Offers Overview"
        hide-footer
    >
      <div v-if="offersOverviewLoading" class="text-center py-5">
        <BSpinner variant="primary" />
        <p class="mt-2">Loading offers overview...</p>
      </div>
      <UserOffersOverview v-else :data="userOfferOverviews"/>
    </b-modal>

    <b-modal ref="click_breakdown_modal" title="Clicks Breakdown" hide-footer>
      <base-table
        ref="clickBreakdownTable"
        :fields="clickBreakdownFields"
        :filterSettings="{}"
        :disableHeader="true"
        :hideSearch="true"
        :isFrontendPagination="true"
        :showPagination="false"
        :firstSearch="false"
        :showSpinner="false"
        :useSpinnerLoading="false"
        url=""
      >
        <template #cell(text)="item">
          <span class="text-nowrap">{{ item.value }}</span>
        </template>
      </base-table>
    </b-modal>
  </div>
</template>

<script>
import useAppConfig from '@core/app-config/useAppConfig'
import UserProfilingBasics from '@/views/users/UserDetails/UserProfilingBasics.vue'
import UserOffersOverview from '@/views/users/UserDetails/UserOffersOverview.vue'
import { BDropdown, BDropdownItem, BButton, BSpinner } from 'bootstrap-vue'

const DETAIL_LINK_NAMES = new Set(['user_details', 'survey_details', 'offer_details'])

const SURVEY_LINK_NAMES = new Set([
  'clicks', 'completes', 'user_profilings', 'survey_list_reloads', 'qualifications',
  'source_extension', 'router_logs', 'user_bonus_offers',
  'survey_invitations', 'clicks_breakdown'
])

const OFFER_LINK_NAMES = new Set([
  'offer_clicks', 'offer_events', 'offer_user_iap_data', 'offer_postback_logs',
  'offer_event_report', 'offer_quality_report', 'offer_performance_analysis'
])

const TITLE_OVERRIDES = {
  survey_list_reloads: 'Survey List Reloads'
}

export default {
  components: { UserOffersOverview, UserProfilingBasics, BDropdown, BDropdownItem, BButton, BSpinner },
  props: {
    useActiveLink: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      activeItem: null,
      userProfilingBasics: null,
      clickBreakdown: null,
      userOfferOverviews: null,
      offersOverviewLoading: false,
      dropdownCloseTimers: {},
      clickBreakdownFields: [
        { label: 'Click Count with Response Status', key: 'text', sortable: false }
      ]
    }
  },
  computed: {
    quickLinks () {
      return this.$store.getters['app/quickLinks']
        .filter(link => this.canAccess(link.permission || link.name))
        .filter(link => {
          if (link.showFunction && typeof this[link.showFunction] === 'function') {
            return this[link.showFunction]()
          }
          return true
        })
    },
    showQuickLinks () {
      return this.$store.getters['app/quickLinkProperties'].display
    },
    projectName () {
      return this.$store.getters['app/quickLinkProperties'].project?.toUpperCase() ?? ''
    },
    model () {
      return this.$store.getters['app/quickLinkProperties'].model
    },
    Id () {
      return this.$store.getters['app/quickLinkProperties']?.id ?? null
    },
    themeSkin () {
      const { skin } = useAppConfig()
      return skin.value
    },
    barThemeClasses () {
      return `${this.themeSkin}-theme-quick-link-styles`
    },
    detailsLinks () {
      return this.quickLinks.filter(l => DETAIL_LINK_NAMES.has(l.name))
    },
    surveyGroupLinks () {
      return this.quickLinks.filter(l => SURVEY_LINK_NAMES.has(l.name) && l.type !== 'function')
    },
    offerGroupLinks () {
      return this.quickLinks.filter(l => OFFER_LINK_NAMES.has(l.name) && l.type !== 'function')
    },
    otherGroupLinks () {
      return this.quickLinks.filter(l =>
        !DETAIL_LINK_NAMES.has(l.name) &&
        !SURVEY_LINK_NAMES.has(l.name) &&
        !OFFER_LINK_NAMES.has(l.name) &&
        l.type !== 'function'
      )
    },
    functionTypeLinks () {
      return this.quickLinks.filter(l => l.type === 'function')
    }
  },
  methods: {
    openDropdown (ref) {
      clearTimeout(this.dropdownCloseTimers[ref])
      this.$refs[ref]?.show()
    },
    scheduleClose (ref) {
      this.dropdownCloseTimers[ref] = setTimeout(() => {
        this.$refs[ref]?.hide()
      }, 150)
    },
    linkProps (link) {
      if (link.type === 'external') {
        return link.url ? { href: link.url, target: link.target || '_blank' } : { disabled: true }
      }
      return { to: this.generateLink(link) }
    },
    showUserProfilingBasicsModal () {
      this.$http
        .get(`/api/user-profiling/${ this.Id }/basics`)
        .then((response) => {
          this.$refs['user_profiling_basics_modal'].show()
          this.userProfilingBasics = response.data.data
        })
        .catch((errors) => {
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
    },
    async showUserOffersOverviewModal () {
      // Clean up expired data before checking
      this.$store.commit('user/CLEANUP_EXPIRED_PREFETCH_DATA')

      // Show modal with loading state
      this.offersOverviewLoading = true
      this.userOfferOverviews = null
      this.$refs['user_offers_overviews_modal'].show()

      try {
        // Check if prefetch is in progress and wait for it
        const prefetchPromise = this.$store.getters['user/getPrefetchPromise'](this.Id)
        if (prefetchPromise) {
          // Wait for prefetch to complete
          await prefetchPromise
        }

        // Check for prefetched data in Vuex store
        const prefetchedData = this.$store.getters['user/getPrefetchedOffersOverview'](this.Id)

        if (prefetchedData) {
          this.userOfferOverviews = prefetchedData
          this.offersOverviewLoading = false
          // Data remains in store until it expires (5 minutes) or limit is reached
          return
        }

        // If no prefetched data or it's stale, fetch fresh data
        const response = await this.$http.get(`/api/user/${ this.Id }/offer-details`)
        this.userOfferOverviews = response.data.data
      } catch (errors) {
        if (errors && errors.response && errors.response.data) {
          this.alertError(errors.response.data.errors)
        }
        this.userOfferOverviews = []
      } finally {
        this.offersOverviewLoading = false
      }
    },
    showClicksBreakdownModal () {
      this.$http.get(`/api/surveys/${this.Id}/click-breakdown`)
        .then(response => {
          this.clickBreakdown = response.data
          // Hydrate BaseTable with local data
          const rows = (this.clickBreakdown || []).map(item => ({
            text: `${item.clicks_ct} ${item.name} (${item.response_status})`
          }))
          this.$nextTick(() => {
            const table = this.$refs.clickBreakdownTable
            if (table) {
              table.params.page = 1
              table.allItems = rows
              table.items = rows
            }
            this.$refs['click_breakdown_modal'].show()
          })
        })
        .catch(error => {
          console.error('Failed to fetch click breakdown:', error)
        })
    },
    isShowSurveyEntryLinksLog () {
      return this.$store.getters['survey/getCurrentSupplierId'] && [7, 107].includes(this.$store.getters['survey/getCurrentSupplierId'])
    },
    getTitle (quickLink) {
      return TITLE_OVERRIDES[quickLink.name] ?? quickLink.meta?.title ?? quickLink.name
    },
    getExternalTitle (externalLink) {
      const prefix = externalLink.url ? '' : 'No '
      return prefix + externalLink.title
    },
    generateLink (routeItem) {
      const routeObject = { name: routeItem.name, params: {}, query: {} }
      const mainRoute = routeItem.meta?.main

      // Define mappings for model and associated parameter names
      const modelIdMap = {
        user: 'userId',
        survey: 'surveyId',
        offer: 'offerId'
      }

      const idParam = modelIdMap[this.model] || 'userId'

      // Set `params` or `query` based on `mainRoute` flag
      if (mainRoute) {
        routeObject.params[idParam] = this.Id
      } else {
        const queryKey = routeItem.meta?.queryFilterObject ? `filter[${idParam}]` : `filter${idParam.charAt(0).toUpperCase() + idParam.slice(1)}`
        routeObject.query[queryKey] = this.Id
      }

      // Add `queryChatFilters` to the route query
      if (routeItem.meta?.queryChatFilters) {
        Object.entries(routeItem.meta.queryChatFilters).forEach(([key, value]) => {
          if (key === 'by_user_id') {
            routeObject.query['filters["user_id"]'] = this.Id
          } else if (Array.isArray(value)) {
            value.forEach((item, index) => {
              routeObject.query[`filters["${key}"][${index}]`] = item
            })
          } else {
            routeObject.query[`filters["${key}"]`] = value
          }
        })
      }

      // Force reload if navigating to the same route
      if (routeItem.name === this.$route.name) {
        routeObject.query['forceReload'] = new Date().getTime()
      }

      return routeObject
    }
  },
  beforeDestroy () {
    Object.values(this.dropdownCloseTimers).forEach(clearTimeout)
  },
  created () {
    // Watch Project value
    this.$store.watch(
      () => this.$store.state.app.quickProperties.project,
      (newValue) => {
        if (newValue) {
          this.$store.commit('app/SET_QUICK_LINKS')
        }
      }
    )

    // Watch Model value
    this.$store.watch(
      () => this.$store.state.app.quickProperties.model,
      (newValue) => {
        if (newValue) {
          this.$store.commit('app/SET_QUICK_LINKS')
        }
      }
    )
  }
}
</script>

<style lang="scss">
.secondary-bar {
  margin: 10px 0;
}

.light-theme-quick-link-styles.box-shadowed {
  box-shadow: 0 0px 2px 0 rgba(34, 41, 47, 0.75);
}

.dark-theme-quick-link-styles.box-shadowed {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.75);
}

.dark-theme-quick-link-styles {
  background: #283046 !important;
}

.light-theme-quick-link-styles {
  background: #ffffff;
}

#quick-links-bar .quick-links-dropdown {
  .dropdown-toggle {
    cursor: pointer;
    padding: 0.3rem 1.25rem;
    font-size: 12px;
    color: inherit;
    border: 1px solid transparent !important;
    box-shadow: none !important;

    &:hover, &:focus, &:active {
      color: inherit;
      background-color: transparent !important;
      border-color: transparent !important;
      box-shadow: none !important;
    }

    &::after {
      display: none;
    }
  }

  .dropdown-item {
    font-size: 12px;

    &, &:hover, &:focus, &:active {
      padding: 0.68rem 1.5rem !important;
    }
  }
}

#quick-links-bar.light-theme-quick-link-styles .quick-links-dropdown {
  .dropdown-toggle,
  .dropdown-item {
    color: #333333 !important;
  }
}

#quick-links-bar.dark-theme-quick-link-styles .quick-links-dropdown {
  .dropdown-toggle,
  .dropdown-item {
    color: #dddddd !important;
  }
}
</style>
